import i18n from 'i18next';
import clinicalSettingsService, { HospitalPreferences } from '@/services/clinicalSettingsService';

/**
 * Utility functions for formatting time and date values.
 * Strictly respects Tenant Regional Settings (dateFormat, timeFormat, timezone)
 * configured in HospitalPreferences, with fallback to i18n locale defaults.
 */

/**
 * Maps i18n language codes to locale codes for date/time formatting
 */
const localeMap: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  hi: 'hi-IN',
  bn: 'bn-BD',
  ur: 'ur-PK',
  vi: 'vi-VN',
  ar: 'ar-SA',
  fr: 'fr-FR',
  zh: 'zh-CN',
};

/**
 * Get the locale code for the current language
 */
export const getLocale = (): string => {
  const currentLang = i18n.language || 'en';
  const baseLang = currentLang.split('-')[0];
  return localeMap[baseLang] || localeMap[currentLang] || 'en-US';
};

/**
 * Get tenant regional preferences safely from clinicalSettingsService
 */
export const getTenantPreferences = (): HospitalPreferences => {
  try {
    return clinicalSettingsService.getHospitalPreferences();
  } catch {
    return {
      language: 'en',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '12h',
      currency: 'INR',
      hospitalName: 'DaAFYA',
      timezone: 'UTC+05:30',
    };
  }
};

/**
 * Converts a timezone preference string (e.g., "UTC+05:30", "Asia/Kolkata")
 * into a valid IANA timezone string or returns undefined for system default.
 */
export const resolveTimezone = (tz?: string): string | undefined => {
  if (!tz) return undefined;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return tz;
  } catch {
    const offsetMap: Record<string, string> = {
      'UTC+05:30': 'Asia/Kolkata',
      'UTC+05:00': 'Asia/Karachi',
      'UTC+06:00': 'Asia/Dhaka',
      'UTC+03:00': 'Asia/Riyadh',
      'UTC+04:00': 'Asia/Dubai',
      'UTC+00:00': 'UTC',
      'UTC-05:00': 'America/New_York',
      'UTC-08:00': 'America/Los_Angeles',
      'UTC+01:00': 'Europe/London',
      'UTC+02:00': 'Europe/Paris',
    };
    return offsetMap[tz] || undefined;
  }
};

/**
 * Helper to check if a string is a date-only representation (e.g. "2026-08-19")
 */
export const isDateOnly = (val: any): boolean => {
  if (typeof val !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(val.trim()) || /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/.test(val.trim());
};

/**
 * Return a stable YYYY-MM-DD calendar key for tenant-local date comparisons.
 * Date-only database values and midnight-UTC serialisations represent a
 * hospital calendar date, so they must not be shifted by the browser timezone.
 */
export const calendarDateKey = (
  value?: string | Date | null,
  timeZone?: string,
): string => {
  if (!value) return '';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      if (isDateOnly(value) || /T00:00:00(?:\.000)?Z?$/.test(value)) {
        return match[1];
      }
    } else {
      // The API timezone interceptor may return values such as
      // "09/23/2026, 05:30:00" rather than an ISO string. These values are
      // already localized, so use the parsed local calendar components.
      const localizedDate = new Date(value);
      if (!Number.isNaN(localizedDate.getTime())) {
        return [
          localizedDate.getFullYear(),
          String(localizedDate.getMonth() + 1).padStart(2, '0'),
          String(localizedDate.getDate()).padStart(2, '0'),
        ].join('-');
      }
      return '';
    }
  }

  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || resolveTimezone(getTenantPreferences().timezone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const todayCalendarDateKey = (timeZone?: string): string =>
  calendarDateKey(new Date(), timeZone);

/** Shift an ISO hospital calendar date without passing through the browser timezone. */
export const shiftCalendarDateKey = (value: string, days: number): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(days)) return '';
  const date = new Date(`${value}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + Math.trunc(days));
  return date.toISOString().slice(0, 10);
};

/**
 * Formats a Date object or timestamp according to the tenant's chosen dateFormat pattern:
 * - "DD/MM/YYYY" -> "19/08/2026" or "19 Aug 2026"
 * - "MM/DD/YYYY" -> "08/19/2026" or "Aug 19, 2026"
 * - "YYYY-MM-DD" -> "2026-08-19"
 */
export const formatDatePattern = (
  date: Date,
  pattern: "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD",
  monthFormat: 'numeric' | '2-digit' | 'short' | 'long' = 'short',
  timeZone?: string
): string => {
  if (monthFormat === 'numeric' || monthFormat === '2-digit') {
    const day = date.toLocaleDateString('en-GB', { day: '2-digit', timeZone });
    const month = date.toLocaleDateString('en-GB', { month: '2-digit', timeZone });
    const year = date.toLocaleDateString('en-GB', { year: 'numeric', timeZone });

    if (pattern === 'MM/DD/YYYY') return `${month}/${day}/${year}`;
    if (pattern === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
    return `${day}/${month}/${year}`;
  }

  const locale = getLocale();
  const day = date.toLocaleDateString(locale, { day: 'numeric', timeZone });
  const month = date.toLocaleDateString(locale, { month: monthFormat, timeZone });
  const year = date.toLocaleDateString(locale, { year: 'numeric', timeZone });

  if (pattern === 'MM/DD/YYYY') return `${month} ${day}, ${year}`;
  if (pattern === 'YYYY-MM-DD') return `${year} ${month} ${day}`;
  return `${day} ${month} ${year}`;
};

/**
 * Format slot_time (HH:mm:ss) to a readable time format using tenant's timeFormat (12h / 24h)
 */
export const formatSlotTime = (slotTime: string): string => {
  if (!slotTime) return "N/A";
  const timePart = slotTime.split(" ")[0];
  const [hours, minutes] = timePart.split(":");
  if (hours && minutes) {
    const hour = parseInt(hours, 10);
    const min = parseInt(minutes, 10);
    const date = new Date();
    date.setHours(hour, min, 0, 0);
    const prefs = getTenantPreferences();
    const hour12 = prefs.timeFormat !== '24h';
    return date.toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12,
    });
  }
  return slotTime;
};

/**
 * Format time from either a datetime string or slot_time using tenant preferences
 */
export const formatTime = (
  dateString?: string,
  slotTime?: string
): string => {
  if (slotTime) return formatSlotTime(slotTime);
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const prefs = getTenantPreferences();
    const timeZone = resolveTimezone(prefs.timezone);
    const hour12 = prefs.timeFormat !== '24h';

    return date.toLocaleTimeString(getLocale(), {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12,
    });
  } catch {
    return "N/A";
  }
};

/**
 * Format a date string or Date object using tenant's configured dateFormat & timezone
 */
export const formatDate = (
  dateString?: string | Date | null,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return "N/A";
  try {
    if (typeof dateString === 'string' && isDateOnly(dateString)) {
      const [y, m, d] = dateString.slice(0, 10).split('-').map(Number);
      const date = new Date(Date.UTC(y, m - 1, d));
      const prefs = getTenantPreferences();
      return options
        ? date.toLocaleDateString(getLocale(), { ...options, timeZone: 'UTC' })
        : formatDatePattern(date, prefs.dateFormat, 'short', 'UTC');
    }

    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return "N/A";

    const prefs = getTenantPreferences();
    const timeZone = resolveTimezone(prefs.timezone);

    if (options) {
      return date.toLocaleDateString(getLocale(), { timeZone, ...options });
    }

    return formatDatePattern(date, prefs.dateFormat, 'short', timeZone);
  } catch {
    return "N/A";
  }
};

/**
 * Format a date with time using tenant's configured dateFormat, timeFormat & timezone
 */
export const formatDateTime = (
  dateString?: string | Date | null,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return "N/A";
  try {
    if (typeof dateString === 'string' && isDateOnly(dateString)) {
      return formatDate(dateString);
    }

    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return "N/A";

    const prefs = getTenantPreferences();
    const timeZone = resolveTimezone(prefs.timezone);
    const hour12 = prefs.timeFormat !== '24h';

    if (options) {
      return date.toLocaleString(getLocale(), {
        timeZone,
        ...(!options.dateStyle && !options.timeStyle ? { hour12 } : {}),
        ...options,
      });
    }

    const dateFormatted = formatDatePattern(date, prefs.dateFormat, 'short', timeZone);
    const timeFormatted = date.toLocaleTimeString(getLocale(), {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12,
    });

    return `${dateFormatted}, ${timeFormatted}`;
  } catch {
    return "N/A";
  }
};

/** Shared wrappers for legacy views: all instant display follows hospital regional settings. */
export const formatHospitalDate = (
  value?: string | Date | number | null,
  options?: Intl.DateTimeFormatOptions,
): string => formatDate(typeof value === 'number' ? new Date(value) : value as string | Date | null, options);

export const formatHospitalDateTime = (
  value?: string | Date | number | null,
  options?: Intl.DateTimeFormatOptions,
): string => formatDateTime(typeof value === 'number' ? new Date(value) : value as string | Date | null, options);

export const formatHospitalTime = (
  value?: string | Date | number | null,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (value == null) return 'N/A';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const prefs = getTenantPreferences();
  const timeZone = resolveTimezone(prefs.timezone);
  const base: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: prefs.timeFormat !== '24h' };
  if (options?.dateStyle || options?.timeStyle) delete base.hour12;
  return date.toLocaleTimeString(getLocale(), { ...base, timeZone, ...options });
};

/**
 * Format a date in short numeric format (e.g. DD/MM/YYYY, MM/DD/YYYY, or YYYY-MM-DD)
 */
export const formatDateShort = (
  dateString?: string | Date | null
): string => {
  if (!dateString) return "N/A";
  try {
    if (typeof dateString === 'string' && isDateOnly(dateString)) {
      const [y, m, d] = dateString.slice(0, 10).split('-').map(Number);
      const date = new Date(Date.UTC(y, m - 1, d));
      const prefs = getTenantPreferences();
      return formatDatePattern(date, prefs.dateFormat, '2-digit', 'UTC');
    }

    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return "N/A";

    const prefs = getTenantPreferences();
    const timeZone = resolveTimezone(prefs.timezone);
    return formatDatePattern(date, prefs.dateFormat, '2-digit', timeZone);
  } catch {
    return "N/A";
  }
};
